import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useIdleTimeout } from "../hooks/useIdleTimeout";
import Logo from "./Logo";
import { Calendar, Users, ClipboardList, LogOut, Bell, Download, Sun, Moon, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "./ui/sheet";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction,
} from "./ui/alert-dialog";

const WARN_MS = 13 * 60 * 1000;   // 13 minutes → show warning
const IDLE_MS = 15 * 60 * 1000;   // 15 minutes → auto logout

function NavItem({ to, icon: Icon, label, active, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${active ? "bg-accent text-foreground border-l-2 border-blue-500" : "text-muted-foreground hover:text-foreground hover:bg-accent/60"}`}>
      <Icon className="h-4 w-4" /> {label}
    </Link>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <Button variant="ghost" size="sm" onClick={toggle} aria-label="Toggle theme" data-testid="theme-toggle">
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [notes, setNotes] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const notesLoaded = useRef(false);

  // Fetch notifications on demand only: once on first page mount, then on manual "Refresh".
  // NO background polling or intervals (optimizes backend credit usage).
  const loadNotes = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotes(data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    if (!notesLoaded.current && user) {
      notesLoaded.current = true;
      loadNotes();
    }
  }, [user]);

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("dataeko_token");
    nav("/login?session=expired");
  };

  const { warning, extend } = useIdleTimeout({
    idleMs: IDLE_MS,
    warnMs: WARN_MS,
    enabled: !!user,
    onTimeout: handleLogout,
  });

  const unread = notes.filter(n => !n.read).length;
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const isAccounts = user?.role === "accounts";

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-60 shrink-0 border-r border-border bg-card flex flex-col">
        <div className="px-5 py-6 border-b border-border"><Logo size="md" /></div>
        <nav className="flex-1 p-3 space-y-1">
          {!isAccounts && !isAdmin && (
            <NavItem to="/timesheet" icon={Calendar} label="My Timesheet" active={loc.pathname.startsWith("/timesheet")} testid="nav-timesheet" />
          )}
          {isAdmin && <>
            <NavItem to="/admin" icon={ClipboardList} label="Approvals" active={loc.pathname === "/admin"} testid="nav-admin-approvals" />
            {isSuperAdmin && <NavItem to="/admin/users" icon={Users} label="Users" active={loc.pathname.startsWith("/admin/users")} testid="nav-admin-users" />}
            <NavItem to="/timesheet" icon={Calendar} label="My Timesheet" active={loc.pathname.startsWith("/timesheet")} testid="nav-timesheet" />
          </>}
          {isAccounts && <>
            <NavItem to="/accounts" icon={ClipboardList} label="All Timesheets" active={loc.pathname === "/accounts"} testid="nav-accounts-list" />
            <NavItem to="/accounts/exports" icon={Download} label="Exports" active={loc.pathname === "/accounts/exports"} testid="nav-accounts-exports" />
          </>}
        </nav>

        <div className="p-3 border-t border-border">
          <div className="px-3 py-3 text-xs">
            <div className="text-foreground font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-muted-foreground truncate font-mono">{user?.email}</div>
            <div className="mt-2 inline-block px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:text-blue-300 text-[10px] uppercase tracking-widest">{user?.role === "super_admin" ? "SUPER ADMIN" : user?.role}</div>
          </div>
          <button onClick={async () => { await logout(); nav("/login"); }}
            className="w-full flex items-center gap-2 text-sm px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/60"
            data-testid="sidebar-logout-button">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col">
        <header className="h-14 border-b border-border flex items-center justify-between px-6 bg-background/80 backdrop-blur sticky top-0 z-10">
          <div className="text-xs text-muted-foreground font-mono uppercase tracking-[0.18em]">Dataeko Timesheet</div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Sheet open={panelOpen} onOpenChange={(o) => { setPanelOpen(o); if (o) loadNotes(); }}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="relative" data-testid="notifications-button">
                  <Bell className="h-4 w-4" />
                  {unread > 0 && <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center" data-testid="notifications-unread-count">{unread}</span>}
                </Button>
              </SheetTrigger>
              <SheetContent className="bg-card border-border text-foreground">
                <SheetHeader><SheetTitle className="text-foreground font-display">Notifications</SheetTitle></SheetHeader>
                <div className="mt-4 flex justify-between items-center">
                  <Button size="sm" variant="ghost" onClick={loadNotes} data-testid="notifications-refresh">
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
                  </Button>
                  <Button size="sm" variant="ghost" onClick={async () => { await api.post("/notifications/read-all"); loadNotes(); }} data-testid="mark-all-read">Mark all read</Button>
                </div>
                <div className="mt-2 space-y-2 overflow-y-auto max-h-[80vh]">
                  {notes.length === 0 && <div className="text-sm text-muted-foreground p-4">No notifications yet</div>}
                  {notes.map(n => (
                    <div key={n.id} className={`p-3 rounded-md border ${n.read ? "border-border bg-muted/30" : "border-blue-500/40 bg-blue-500/[0.06]"}`}>
                      <div className="text-sm">{n.message}</div>
                      <div className="text-[10px] text-muted-foreground mt-1 font-mono">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </header>
        <div className="flex-1 overflow-auto">{children}</div>
      </main>

      <AlertDialog open={warning} onOpenChange={(o) => { if (!o) extend(); }}>
        <AlertDialogContent className="bg-card border-border text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Session expiring in 2 minutes</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              You've been inactive for a while. For security, we'll sign you out automatically in 2 minutes unless you stay here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="ghost" onClick={handleLogout} data-testid="idle-logout">Sign out now</Button>
            <AlertDialogAction onClick={extend} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="idle-stay">Stay signed in</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
